import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useTheme, useMediaQuery } from '@mui/material';
import { bookingsAPI } from '../utils/api';
import NoShowChargeModal from './NoShowChargeModal';

function CalendarView({ onMarkAsPaid, onStatusChange }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [events, setEvents] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noShowBooking, setNoShowBooking] = useState(null);

  useEffect(() => {
    fetchAllBookings();
  }, []);

  const fetchAllBookings = async () => {
    setLoading(true);
    try {
      // Fetch all bookings without date filter to show in calendar
      const response = await bookingsAPI.getAll({});
      const bookings = response.data;

      // Transform bookings to calendar events
      const calendarEvents = bookings.map(booking => ({
        id: booking.id,
        title: `${booking.customer_name} - ${booking.service_name}`,
        start: `${booking.booking_date}T${booking.start_time}`,
        end: `${booking.booking_date}T${booking.end_time}`,
        backgroundColor: getStatusColor(booking.status, booking.payment_status),
        borderColor: getStatusColor(booking.status, booking.payment_status),
        extendedProps: {
          ...booking
        }
      }));

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching bookings for calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  // Brand colors matching Deep Red & Cream theme
  const getStatusColor = (status, paymentStatus) => {
    if (status === 'cancelled') return '#C62828'; // Error red
    if (status === 'completed') return '#6B6B6B'; // Warm gray
    if (paymentStatus === 'pending' || !paymentStatus) return '#E09F3E'; // Warning amber
    if (status === 'confirmed') return '#4A7C59'; // Success sage green
    return '#5B7B8A'; // Info teal default
  };

  const handleEventClick = (clickInfo) => {
    setSelectedBooking(clickInfo.event.extendedProps);
  };

  const handleCloseModal = () => {
    setSelectedBooking(null);
  };

  const handleMarkPaid = async () => {
    if (selectedBooking && onMarkAsPaid) {
      await onMarkAsPaid(selectedBooking.id);
      fetchAllBookings();
      setSelectedBooking(null);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (selectedBooking && onStatusChange) {
      await onStatusChange(selectedBooking.id, newStatus);
      fetchAllBookings();
      setSelectedBooking(null);
    }
  };

  const handleOpenNoShowModal = () => {
    if (!selectedBooking) return;
    setNoShowBooking(selectedBooking);
    setSelectedBooking(null);
  };

  const handleNoShowChargeSuccess = () => {
    fetchAllBookings();
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading calendar...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#4A7C59', borderRadius: '3px' }}></div>
          <span>Confirmed & Paid</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#E09F3E', borderRadius: '3px' }}></div>
          <span>Unpaid</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#C62828', borderRadius: '3px' }}></div>
          <span>Cancelled</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#6B6B6B', borderRadius: '3px' }}></div>
          <span>Completed</span>
        </div>
      </div>

      {/* Calendar */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: isMobile ? '100%' : 'auto' }}>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={isMobile ? "timeGridDay" : "timeGridWeek"}
            headerToolbar={isMobile ? {
              left: 'prev,next',
              center: 'title',
              right: 'timeGridDay,timeGridWeek'
            } : {
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            events={events}
            eventClick={handleEventClick}
            height="auto"
            contentHeight={isMobile ? 500 : 'auto'}
            slotMinTime="08:00:00"
            slotMaxTime="20:00:00"
            allDaySlot={false}
            weekends={true}
            nowIndicator={true}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            titleFormat={isMobile ? { month: 'short', day: 'numeric' } : { month: 'long', year: 'numeric' }}
          />
        </div>
      </div>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={handleCloseModal}
        >
          <div
            className="card"
            style={{
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '1rem' }}>Booking Details</h2>

            <div style={{ marginBottom: '1rem' }}>
              <p><strong>Customer:</strong> {selectedBooking.customer_name}</p>
              <p><strong>Email:</strong> {selectedBooking.customer_email}</p>
              <p><strong>Phone:</strong> {selectedBooking.customer_phone}</p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <p><strong>Service:</strong> {selectedBooking.service_name}</p>
              <p><strong>Price:</strong> £{selectedBooking.service_price}</p>
              <p><strong>Date:</strong> {selectedBooking.booking_date}</p>
              <p><strong>Time:</strong> {selectedBooking.start_time} - {selectedBooking.end_time}</p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <p>
                <strong>Status:</strong>{' '}
                <span className={`badge badge-${selectedBooking.status === 'confirmed' ? 'success' : selectedBooking.status === 'cancelled' ? 'danger' : 'warning'}`}>
                  {selectedBooking.status}
                </span>
              </p>
              <p>
                <strong>Payment:</strong>{' '}
                <span className={`badge badge-${selectedBooking.payment_status === 'completed' ? 'success' : 'warning'}`}>
                  {selectedBooking.payment_status || 'pending'}
                </span>
                {selectedBooking.payment_method && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#6B6B6B' }}>
                    via {selectedBooking.payment_method}
                  </span>
                )}
              </p>
              {selectedBooking.stripe_payment_method_id && (
                <p style={{ color: '#4A7C59', fontSize: '0.875rem' }}>
                  Card on file for no-show protection
                </p>
              )}
            </div>

            {selectedBooking.notes && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#FAF8F5', borderRadius: '4px' }}>
                <strong>Notes:</strong> {selectedBooking.notes}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {selectedBooking.payment_status !== 'completed' && (
                <button className="btn btn-success" onClick={handleMarkPaid}>
                  Mark Paid
                </button>
              )}
              {selectedBooking.stripe_payment_method_id && selectedBooking.payment_status !== 'completed' && (
                <button
                  className="btn"
                  style={{ background: '#C62828', color: 'white' }}
                  onClick={handleOpenNoShowModal}
                >
                  Charge No-Show
                </button>
              )}
              {selectedBooking.status !== 'completed' && selectedBooking.status !== 'cancelled' && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleStatusUpdate('completed')}
                >
                  Mark Completed
                </button>
              )}
              {selectedBooking.status !== 'cancelled' && (
                <button
                  className="btn btn-danger"
                  onClick={() => handleStatusUpdate('cancelled')}
                >
                  Cancel
                </button>
              )}
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No-Show Charge Modal */}
      <NoShowChargeModal
        booking={noShowBooking}
        isOpen={!!noShowBooking}
        onClose={() => setNoShowBooking(null)}
        onSuccess={handleNoShowChargeSuccess}
      />
    </div>
  );
}

export default CalendarView;
